if (!customElements.get('product-list-tabs')) {
  class ProductListTabs extends HTMLElement {
    prewarmedPanels = new Set();

    refreshFrame = 0;

    warmupHandle = null;

    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-product-list-tab-button]'));
      this.panels = Array.from(this.querySelectorAll('[data-product-list-tab-panel]'));
      this.externalButton = this
        .closest('.section-resource-list')
        ?.querySelector('.section-resource-list__content [data-button-block]');

      this.externalButtonDefaults = this.externalButton
        ? {
            label: this.externalButton.textContent?.trim() ?? '',
            href: this.externalButton.getAttribute('href') ?? '',
            target: this.externalButton.getAttribute('target'),
            rel: this.externalButton.getAttribute('rel'),
          }
        : null;

      this.addEventListener('click', this.handleClick);

      const initialTabId =
        this.dataset.activeTabId ||
        this.buttons[0]?.dataset.productListTabId ||
        this.panels[0]?.dataset.productListTabId;

      if (initialTabId) {
        this.select(initialTabId);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);

      if (this.refreshFrame) {
        cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = 0;
      }

      if (this.warmupHandle != null) {
        if ('cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.warmupHandle);
        } else {
          clearTimeout(this.warmupHandle);
        }

        this.warmupHandle = null;
      }
    }

    handleClick = (event) => {
      const button = event.target instanceof HTMLElement ? event.target.closest('[data-product-list-tab-button]') : null;
      if (!(button instanceof HTMLButtonElement)) return;

      event.preventDefault();
      this.select(button.dataset.productListTabId ?? '');
    };

    select(tabId) {
      if (!tabId) return;

      this.buttons.forEach((button) => {
        const isActive = button.dataset.productListTabId === tabId;
        button.classList.toggle('product-list-tabs__tab--active', isActive);
        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.productListTabId === tabId;
        panel.hidden = !isActive;
      });

      const activePanel = this.panels.find((panel) => panel.dataset.productListTabId === tabId);
      if (activePanel instanceof HTMLElement) {
        this.preparePanel(activePanel, { refreshSlideshows: true });
      }

      this.scheduleWarmup(tabId);
      this.updateExternalButton(tabId);
    }

    scheduleWarmup(activeTabId) {
      if (this.warmupHandle != null) {
        if ('cancelIdleCallback' in window) {
          window.cancelIdleCallback(this.warmupHandle);
        } else {
          clearTimeout(this.warmupHandle);
        }
      }

      const warmup = () => {
        this.warmupHandle = null;

        this.panels.forEach((panel) => {
          if (!(panel instanceof HTMLElement)) return;
          if (panel.dataset.productListTabId === activeTabId) return;

          this.preparePanel(panel);
        });
      };

      if ('requestIdleCallback' in window) {
        this.warmupHandle = window.requestIdleCallback(warmup, { timeout: 1200 });
      } else {
        this.warmupHandle = window.setTimeout(warmup, 250);
      }
    }

    preparePanel(panel, { refreshSlideshows = false } = {}) {
      const panelId = panel.dataset.productListTabId ?? '';

      if (!this.prewarmedPanels.has(panelId)) {
        this.prewarmedPanels.add(panelId);

        panel.querySelectorAll('product-card').forEach((card) => {
          const primaryImage = card.querySelector('.product-media__image');
          if (!(primaryImage instanceof HTMLImageElement)) return;

          if (primaryImage.loading === 'lazy') {
            primaryImage.loading = 'eager';
          }
        });
      }

      if (!refreshSlideshows) return;

      if (this.refreshFrame) {
        cancelAnimationFrame(this.refreshFrame);
      }

      this.refreshFrame = requestAnimationFrame(() => {
        this.refreshFrame = requestAnimationFrame(() => {
          panel.querySelectorAll('slideshow-component').forEach((slideshow) => {
            if (typeof slideshow.refresh === 'function') {
              slideshow.refresh();
            }
          });
        });
      });
    }

    updateExternalButton(tabId) {
      if (!(this.externalButton instanceof HTMLAnchorElement)) return;

      const activePanel = this.panels.find((panel) => panel.dataset.productListTabId === tabId);
      const hasCustomButtonSettings =
        activePanel?.dataset.buttonLabel !== undefined || activePanel?.dataset.buttonLink !== undefined;

      if (!hasCustomButtonSettings) {
        const defaultLabel = this.externalButtonDefaults?.label ?? '';
        const defaultHref = this.externalButtonDefaults?.href ?? '';

        this.externalButton.textContent = defaultLabel;
        this.externalButton.hidden = defaultLabel === '' || defaultHref === '';

        if (defaultHref) {
          this.externalButton.setAttribute('href', defaultHref);
          this.externalButton.removeAttribute('role');
          this.externalButton.removeAttribute('aria-disabled');
        } else {
          this.externalButton.removeAttribute('href');
          this.externalButton.setAttribute('role', 'link');
          this.externalButton.setAttribute('aria-disabled', 'true');
        }

        if (this.externalButtonDefaults?.target) {
          this.externalButton.setAttribute('target', this.externalButtonDefaults.target);
        } else {
          this.externalButton.removeAttribute('target');
        }

        if (this.externalButtonDefaults?.rel) {
          this.externalButton.setAttribute('rel', this.externalButtonDefaults.rel);
        } else {
          this.externalButton.removeAttribute('rel');
        }

        return;
      }

      const nextLabel = activePanel?.dataset.buttonLabel?.trim() ?? '';
      const nextHref = activePanel?.dataset.buttonLink?.trim() ?? '';
      const hasValidButton = nextLabel !== '' && nextHref !== '';

      if (!hasValidButton) {
        this.externalButton.textContent = this.externalButtonDefaults?.label ?? '';
        this.externalButton.removeAttribute('href');
        this.externalButton.hidden = true;
        this.externalButton.setAttribute('role', 'link');
        this.externalButton.setAttribute('aria-disabled', 'true');
        return;
      }

      this.externalButton.textContent = nextLabel;
      this.externalButton.setAttribute('href', nextHref);
      this.externalButton.hidden = false;
      this.externalButton.removeAttribute('role');
      this.externalButton.removeAttribute('aria-disabled');

      if (this.externalButtonDefaults?.target) {
        this.externalButton.setAttribute('target', this.externalButtonDefaults.target);
      } else {
        this.externalButton.removeAttribute('target');
      }

      if (this.externalButtonDefaults?.rel) {
        this.externalButton.setAttribute('rel', this.externalButtonDefaults.rel);
      } else {
        this.externalButton.removeAttribute('rel');
      }
    }
  }

  customElements.define('product-list-tabs', ProductListTabs);
}
